import axios, { InternalAxiosRequestConfig } from "axios";
import { useCallback, useContext, useEffect, useState } from "react";

import { useSnackbar } from "providers/SnackbarProvider";
import { errorApi } from "_constants";
import {
  deleteTokensFromStorage,
  getDecodedToken,
  getRefreshTokenFromStorage,
  getTokenFromStorage,
  setRefreshTokenInStorage,
  setTokenInStorage,
} from "./tokenUtils";
import { useSnackbarMessages } from "hooks";
import { useNavigate } from "react-router-dom";
import { LoginResponse } from "interfaces";
import { useAppDispatch } from "store/store";
import { setUserProfile } from "store/slicer/ProfiloSlice";
import { useLoaderSpinner } from "providers/LoaderSpinnerProvider";
import { DetailPanelContext } from "providers/DetailPanelProvider";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL_API,
});

const apiToBeAuthorized = [
  "api/auth/logout",
  "api/users/get-users",
  "api/users/add",
  "api/users/delete",
  "api/users/update",
  "api/sources/get-sources",
  "api/sources/add",
  "api/sources/delete",
  "api/topic/get-sources",
  "api/topic/add",
  "api/topic/get-topics-list",
  "api/topic/get-topics",
  "api/topic/delete",
  "api/topic/update",
  "api/sources/check-name",
  "api/sources/check-isbn",
  "api/sources/upload",
  "api/sources/multipart/initiate",
  "api/sources/multipart/upload-part",
  "api/sources/multipart/complete",
  "api/sources/multipart/abort",
  "api/projects/add",
  "api/projects/update",
  "api/projects/add-sources",
  "api/projects/list",
  "api/projects/header-detail",
  "api/projects/sources-list",
  "api/projects/detail",
  "api/projects/preferred",
  "api/projects/users-to-share",
  "api/projects/share",
  "api/projects/remove-user",
  "api/projects/delete",
  "api/projects/leave",
  "api/projects/accept-invitation",
  "api/projects/archive-sources-list",
  "api/sources/update",
  "api/sources/detail",
  "api/projects/check-source",
  "api/projects/check-last-reset-date",
  "api/projects/chat-inactive-answers",
  "api/projects/pinned-answers",
  "api/projects/pinned-answer-detail",
  "api/projects/pin-answers",
  "api/projects/share-pinned-answers",
  "api/projects/update-pinned-answer-detail",
  "api/projects/codes-list",
  "api/projects/prompt",
  "api/projects/code-detail",
  "api/projects/get-all-reports",
  "api/auth/update-password",
  "api/auth/update",
  "api/projects/chat-history",
  "api/auth/delete",
  "api/users/delete",
  "api/projects/report",
  "api/projects/report-answer",
  "api/projects/pinned-answers",
  "api/projects/pinned-answer-detail",
  "api/sources/download",
  "api/projects/download",
  "api/projects/refresh-prompt",
  "api/projects/delete-source",
  "api/projects/check-report",
  "api/sources/check-source-status",
  "api/sources/retry",
];

const apiToNotShowLoaderSpinner = [
  "api/users/get-users",
  "api/sources/get-sources",
  "api/topic/get-topics",
  "api/topic/get-topics-list",
  "api/sources/upload",
  "api/sources/multipart/initiate",
  "api/sources/multipart/upload-part",
  "api/sources/multipart/complete",
  "api/sources/multipart/abort",
  "api/projects/list",
  "api/projects/preferred",
  "api/projects/check-source",
  "api/projects/codes-list",
  "api/projects/prompt",
  "api/projects/get-all-reports",
  "api/auth/delete",
  "api/sources/download",
  "api/projects/download",
  "api/projects/refresh-prompt",
  "api/auth/logout",
  "api/projects/check-report",
  "api/projects/archive-sources-list",
  "api/projects/check-source-status",
];

const AxiosInterceptor = ({ children }: any) => {
  const [isSet, setIsSet] = useState(false);
  const dispatch = useAppDispatch();
  const { showDialogError, showDialogForbidden } = useSnackbarMessages();
  const { showSnackbar } = useSnackbar();
  const { toggleLoaderSpinner } = useLoaderSpinner();
  const { hidePanel } = useContext(DetailPanelContext);

  const navigate = useNavigate();

  const resInterceptor = useCallback((response: any) => {
    toggleLoaderSpinner(false);
    return response;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let renewFunction: any = undefined;

  const renewToken = async () => {
    const refreshToken = getRefreshTokenFromStorage();
    const response = await axiosInstance.post<LoginResponse>(
      "api/auth/refresh-token",
      {
        refreshToken: refreshToken,
      }
    );

    const token = response.data.accessToken;

    const newRefreshToken = response.data.refreshToken;
    return [token, newRefreshToken];
  };

  const errInterceptor = useCallback(async (error: any) => {
    if (error.response?.status === 400 || error.response?.status > 499) {
      showDialogError();
      toggleLoaderSpinner(false);
      return Promise.reject(error);
    } else {
      const url = error.response?.config.url?.split("?")[0];
      if (
        error.response?.status === 401 &&
        url &&
        apiToBeAuthorized.includes(url)
      ) {
        let decodedToken = getDecodedToken();
        let refreshTokenValue = getRefreshTokenFromStorage();
        const originalRequest = error.config;

        if (
          decodedToken &&
          refreshTokenValue &&
          !originalRequest._retry &&
          originalRequest.url !== "api/auth/refresh-token"
        ) {
          originalRequest._retry = true;
          let currentTime = new Date().getTime();
          let diff = decodedToken.exp * 1000 - currentTime;

          if (diff < 60000) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            if (!renewFunction) renewFunction = renewToken();
            const [newToken, newRefreshToken] = await renewFunction;
            try {
              setTokenInStorage(newToken);
              setRefreshTokenInStorage(newRefreshToken);
              let decodedToken = getDecodedToken();
              dispatch(
                setUserProfile({
                  token: newToken,
                  firstName: decodedToken?.firstName,
                  lastName: decodedToken?.lastName,
                  email: decodedToken?.userName,
                  role: decodedToken?.role,
                })
              );
              axiosInstance.defaults.headers.common[
                "Authorization"
              ] = `Bearer ${newToken}`;
              return axiosInstance(originalRequest);
            } catch (refreshTokenError) {
              deleteTokensFromStorage();
              hidePanel();
              navigate("/");
              toggleLoaderSpinner(false);
              return Promise.reject(refreshTokenError);
            }
          }
        } else {
          deleteTokensFromStorage();
          hidePanel();
          navigate("/");
          toggleLoaderSpinner(false);
          return Promise.reject(error);
        }
      } else {
        let url = error.config?.url.split("?")[0];
        if (url !== "api/auth/refresh-token" && url !== "api/auth/logout") {
          if (errorApi[url] && errorApi[url][error.response?.status]) {
            showSnackbar({
              severity:
                errorApi[url][error.response?.status].severity ?? "error",
              summary: errorApi[url][error.response?.status].summary,
              detail: errorApi[url][error.response?.status].detail,
              life: 5000,
            });
          } else {
            if (error.response?.status === 403) {
              showDialogForbidden();
            }
          }
        } else {
          deleteTokensFromStorage();
          hidePanel();
          navigate("/");
          toggleLoaderSpinner(false);
          return Promise.reject(error);
        }
      }
      toggleLoaderSpinner(false);
      return Promise.reject(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestInterceptor = useCallback(
    async (config: InternalAxiosRequestConfig) => {
      let token = getTokenFromStorage();
      const url = config.url?.split("?")[0];

      if (url && !apiToNotShowLoaderSpinner.includes(url)) {
        toggleLoaderSpinner(true);
      }

      if (token && url && apiToBeAuthorized.includes(url)) {
        config.headers.Authorization = `Bearer ${token}`;

        return config;
      }

      return config;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    axiosInstance.interceptors.request.use(requestInterceptor, (error) => {
      return Promise.reject(error);
    });

    const interceptor = axiosInstance.interceptors.response.use(
      resInterceptor,
      errInterceptor
    );
    setIsSet(true);

    return () => {
      axiosInstance.interceptors.response.eject(interceptor);
    };
  }, [errInterceptor, requestInterceptor, resInterceptor]);

  return isSet && children;
};

export default axiosInstance;
export { AxiosInterceptor };
